import "../../App.css";

import { zodResolver } from "@hookform/resolvers/zod";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Button, Chip, Pagination, Tooltip } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { normalizePayer } from "../../api/filters/claimFilters";
import useQueryClient from "../../api/query";
import { formatMoney } from "../../formatters";
import { ReviewStatus, WieldyStatus } from "../../status";
import type { BackendFilters } from "../../utils/backendFilters";
import useDevMode from "../../utils/devmode";
import {
  camelToSnakeCase,
  enumToStrings,
  isoToDate,
  sortAlphabetically,
  toValueOptions,
} from "../../utils/utils";
import AddEntityActions from "../ClaimsTable/AddEntityActions";
import DataTable, {
  type CellParams,
  type DataTableColDef,
  loadTableState,
  type PaginationModel,
  type SortModel,
  sortRows,
  useServerFilterModel,
} from "../DataTable";
import { renderCellRedacted } from "../redact";
import CustomNoRowsOverlay from "../TableNoDataOverlay";
import AIPaymentForm from "./AIPaymentForm";
import {
  type AddPaymentFormFields,
  AIFormSchemaBase,
  type AIPaymentFormFields,
  defaultPaymentFields,
  editablePaymentFields,
  paymentFormSchemaBase,
} from "./CrudForms.zod";
import PaymentForm from "./PaymentForm";

const DEFAULT_SORT: SortModel = [{ field: "paymentDate", sort: "desc" }];

const getColumnDefaultVisibility = () => ({
  date: true,
  eopPaymentAmount: true,
  isPosted: true,
  payee: false,
  payer: true,
  payerPaymentId: true,
  paymentStatus: true,
  paymentType: true,
  practice: true,
  productType: false,
  wieldyStatus: false,
});

const renderPosted = (props: CellParams<PaymentMessage>) => (
  <Checkbox
    disabled
    color="primary"
    checked={Boolean(props.value)}
    inputProps={{
      "aria-labelledby": "isPosted",
    }}
    sx={{
      "&.Mui-disabled": {
        color: (theme) => theme.palette.primary.main,
      },
    }}
  />
);

const getColumns = (
  payerValueOptions: string[],
  practiceValueOptions: string[],
  onSelectPayment: (payment: PaymentMessage) => void,
  editPayment: (payment: PaymentMessage) => void,
  validatePayment?: (payment: PaymentMessage) => void,
  devMode?: boolean,
) => {
  const defaultColumns: DataTableColDef<PaymentMessage>[] = [
    {
      flex: 1,
      field: "practiceDisplayName",
      headerName: "Practice",
      type: "singleSelect",
      filterable: false,
      valueOptions: practiceValueOptions.length
        ? practiceValueOptions
        : undefined,
    },
    {
      flex: 1,
      type: "date",
      field: "paymentDate",
      headerName: "Payment Date",
      valueGetter: (_value, row) =>
        row.paymentDate ? isoToDate(row.paymentDate) : null,
    },
    {
      flex: 1,
      field: "payer",
      headerName: "Payer",
      type: "singleSelect",
      valueGetter: (_value, row) => normalizePayer(row.payer),
      renderCell: (params) => (params.value as string | null) ?? "-",
      valueOptions: payerValueOptions.length ? payerValueOptions : undefined,
    },
    {
      flex: 1,
      field: "payee",
      headerName: "Payee",
    },
    {
      flex: 1,
      field: "payerPaymentId",
      headerName: "Payment ID",
      renderCell: renderCellRedacted,
    },
    {
      flex: 1,
      field: "paymentStatus",
      headerName: "Payment Status",
    },
    {
      flex: 1,
      field: "paymentType",
      headerName: "Payment Type",
    },
    {
      flex: 1,
      field: "productType",
      headerName: "Product Type",
    },
    {
      flex: 1,
      type: "number",
      field: "eopPaymentAmount",
      headerName: "Payment Amount",
      valueFormatter: (value) =>
        value === null || value === undefined
          ? "-"
          : formatMoney(value as number),
      renderCell: renderCellRedacted,
    },
    {
      flex: 1,
      field: "isPosted",
      type: "boolean",
      headerName: "All Posted",
      renderCell: renderPosted,
    },
    {
      flex: 1,
      field: "details",
      headerName: "Details",
      sortable: false,
      filterable: false,
      hideable: false,
      disableColumnMenu: true,
      disableExport: true,
      renderCell: (params) => (
        // TODO: Replace with a React Router Link.
        <Button
          data-testid="toPaymentDetails"
          type="button"
          variant="outlined"
          style={{
            cursor: "pointer",
            width: "100%",
          }}
          onClick={() => {
            onSelectPayment(params.row);
          }}
        >
          <NavigateNextIcon
            style={{ cursor: "pointer" }}
            aria-label="Select Row"
          />
        </Button>
      ),
    },
  ];

  const devModeOnlyColumns: DataTableColDef<PaymentMessage>[] = [
    {
      flex: 0.5,
      field: "edit",
      headerName: "Edit",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableExport: true,
      renderCell: (params) => (
        <IconButton
          onClick={() => {
            editPayment(params.row);
          }}
        >
          <EditIcon />
        </IconButton>
      ),
    },
    {
      flex: 1,
      field: "reviewStatus",
      type: "singleSelect",
      headerName: "Review Status",
      valueOptions: enumToStrings(ReviewStatus).map((reviewStatus) =>
        reviewStatus.toUpperCase(),
      ),
      renderCell: (params) => {
        const { reviewStatus, reviewReason } = params.row;
        if (
          validatePayment &&
          reviewReason &&
          reviewReason.length > 0 &&
          [ReviewStatus.MODIFIED, ReviewStatus.REVIEW].includes(
            reviewStatus.toUpperCase(),
          )
        ) {
          return (
            <Tooltip title={reviewReason || ""} arrow>
              <Button
                variant="contained"
                onClick={() => {
                  if (validatePayment) {
                    validatePayment(params.row);
                  }
                }}
              >
                {reviewStatus}
              </Button>
            </Tooltip>
          );
        }
        return <div>{reviewStatus}</div>;
      },
    },
    {
      flex: 1,
      field: "wieldyStatus",
      headerName: "Wieldy Status",
      type: "singleSelect",
      valueOptions: enumToStrings(WieldyStatus).map((wieldyStatus) =>
        wieldyStatus.toUpperCase(),
      ),
    },
    {
      flex: 1,
      field: "reconciled",
      headerName: "Reconciled",
      type: "singleSelect",
      valueOptions: ["Yes", "No"],
      valueGetter: (_value, row) => (row.bankTransactionKey ? "Yes" : "No"),
      renderCell: (params) =>
        params.row.bankTransactionKey ? (
          <Chip
            icon={<CheckIcon />}
            label="Reconciled"
            size="small"
            variant="outlined"
            sx={{
              color: "#2e7d32",
              borderColor: "#2e7d32",
              "& .MuiChip-icon": { color: "#2e7d32" },
            }}
          />
        ) : null,
    },
  ];

  if (devMode) {
    defaultColumns.push(...devModeOnlyColumns);
  }
  return defaultColumns;
};

interface PaymentsTableProps {
  practices: PracticeMessage[];
  supportedPayers: CredentialsSupportedPayersMessage[];
  supportedSources: SourceMessage[];
  isLoading: boolean;
  payments: PaymentMessage[];
  onSelectPayment: (payment: PaymentMessage) => void;
  persistKey?: string;
  onSavePayment: (payment: PaymentMessage, file: File | null) => Promise<void>;
  onSaveAI: (aiMessage: AIMessage, file: File) => Promise<void>;
  validatePayment?: (payment: PaymentMessage) => void;
  onFilterChange?: (filters: BackendFilters) => void;
  pagination?: {
    page: number;
    pageSize: number;
    rowCount: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
  };
}

export default function PaymentsTable({
  practices,
  supportedPayers,
  supportedSources,
  isLoading,
  payments,
  onSelectPayment,
  persistKey,
  onSavePayment,
  onSaveAI,
  validatePayment,
  onFilterChange,
  pagination,
}: PaymentsTableProps) {
  const [openPaymentModal, setOpenPaymentModal] = React.useState(false);
  const [openAIModal, setOpenAIModal] = React.useState(false);
  const [paymentFileToUpload, setPaymentFileToUpload] =
    React.useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [aiFileToUpload, setAIFileToUpload] = React.useState<File | null>(null);
  const [aiFileError, setAIFileError] = useState<string | null>(null);
  const [isEdit, setIsEdit] = React.useState(false);

  const queryClient = useQueryClient();
  const devMode = useDevMode();

  const effectivePage = pagination?.page ?? 1;
  const effectivePageSize = pagination?.pageSize ?? 10;
  const rowCount = pagination?.rowCount ?? payments.length;

  const { filterModel, onFilterModelChange } = useServerFilterModel({
    persistKey,
    mapField: camelToSnakeCase,
    onFiltersChange: onFilterChange,
  });

  const [sortModel, setSortModel] = useState<SortModel>(
    () =>
      (persistKey && loadTableState(persistKey)?.sorting?.sortModel) ||
      DEFAULT_SORT,
  );

  const [enumFilterOptions, setEnumFilterOptions] = useState<
    Record<string, string[]>
  >({});

  const {
    control: paymentControl,
    formState: { errors, isSubmitting, isValid },
    handleSubmit: handleSubmitPayment,
    reset,
  } = useForm<AddPaymentFormFields>({
    mode: "onChange",
    reValidateMode: "onChange",
    delayError: 1000,
    resolver: zodResolver(paymentFormSchemaBase),
    defaultValues: defaultPaymentFields,
  });

  const {
    control: aiControl,
    formState: {
      errors: aiErrors,
      isSubmitting: aiIsSubmitting,
      isValid: aiIsValid,
    },
    handleSubmit: aiHandleSubmitPayment,
    reset: aiReset,
  } = useForm<AIPaymentFormFields>({
    mode: "onChange",
    reValidateMode: "onChange",
    delayError: 1000,
    resolver: zodResolver(AIFormSchemaBase),
  });

  const onEditPayment = (payment: PaymentMessage) => {
    setIsEdit(true);
    const formFields = editablePaymentFields.reduce<AddPaymentFormFields>(
      (acc, field) =>
        Object.assign(acc, {
          [field.name]:
            // We pass payer from FE to BE in titlecase but the BE
            // expects all uppercase.
            field.name === "payer"
              ? payment[field.name as keyof typeof payment]?.toUpperCase()
              : (payment[field.name as keyof typeof payment] ??
                defaultPaymentFields[
                  field.name as keyof typeof defaultPaymentFields
                ]),
        }),
      // We only have wieldyId when editing fields so make sure
      // it exists or we'll accidentally CREATE a payment instead
      // since CREATE payment don't have wieldyId and EDITs
      // do
      { wieldyId: payment.wieldyId } as AddPaymentFormFields,
    );
    reset(formFields);
    setOpenPaymentModal(true);
  };

  const onPaymentModal = (open: boolean) => {
    if (!open) {
      setIsEdit(false);
    }
    if (open) {
      reset(defaultPaymentFields);
    }
    setOpenPaymentModal(open);
  };

  const onAIModal = (open: boolean) => {
    if (open) {
      aiReset();
    }
    setOpenAIModal(open);
  };

  // Helper to normalize and deduplicate payer options
  const normalizeEnumFilterOptions = (options: string[]): string[] => {
    const seen = new Set<string>();
    return options
      .map((opt) => normalizePayer(opt))
      .filter((opt) => {
        if (seen.has(opt)) return false;
        seen.add(opt);
        return true;
      });
  };

  // Fetch enum filter options (payer)
  // biome-ignore lint/correctness/useExhaustiveDependencies: adding normalizeEnumFilterOptions, queryClient.getPaymentEnumFilters would change when this runs
  useEffect(() => {
    const fetchEnumOptions = async () => {
      try {
        const options = await queryClient.getPaymentEnumFilters(["payer"], {});
        setEnumFilterOptions(
          Object.fromEntries(
            Object.entries(options).map(([key, value]) => [
              key,
              normalizeEnumFilterOptions(value).sort(sortAlphabetically),
            ]),
          ),
        );
      } catch {
        setEnumFilterOptions({});
      }
    };
    fetchEnumOptions();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding onEditPayment would change when this runs
  const columns = React.useMemo(
    () =>
      getColumns(
        enumFilterOptions.payer || [],
        toValueOptions(payments, "practiceDisplayName", sortAlphabetically),
        onSelectPayment,
        onEditPayment,
        validatePayment,
        devMode,
      ),
    [
      enumFilterOptions.payer,
      payments,
      onSelectPayment,
      validatePayment,
      devMode,
    ],
  );

  const sortedPayments = React.useMemo(
    () => sortRows(payments, columns, sortModel),
    [payments, columns, sortModel],
  );

  const onPaginationModelChange = ({
    page: newPage,
    pageSize: newPageSize,
  }: PaginationModel) => {
    if (newPage + 1 !== effectivePage) pagination?.onPageChange?.(newPage + 1);
    if (newPageSize !== effectivePageSize)
      pagination?.onPageSizeChange?.(newPageSize);
  };

  const onPaymentFormSubmission = handleSubmitPayment(async (data) => {
    if (onSavePayment) {
      await onSavePayment(data as PaymentMessage, paymentFileToUpload);
    }
  });

  const onAIFormSubmission = aiHandleSubmitPayment(async (data) => {
    if (onSaveAI && aiFileToUpload) {
      await onSaveAI(data as AIMessage, aiFileToUpload);
    }
  });

  const paymentHasErrors = Object.keys(errors).length > 0;
  const paymentIsDisabled = isSubmitting || paymentHasErrors || !isValid;

  const AIHasErrors = Object.keys(aiErrors).length > 0;
  const aiIsDisabled =
    aiIsSubmitting ||
    AIHasErrors ||
    !aiIsValid ||
    aiFileError ||
    !aiFileToUpload;

  return (
    <DataTable
      loading={isLoading}
      persistKey={persistKey}
      rows={sortedPayments}
      columns={columns}
      getRowId={(row) => row.wieldyId}
      rowCount={rowCount}
      paginationModel={{ page: effectivePage - 1, pageSize: effectivePageSize }}
      pageSizeOptions={[10, 20, 40]}
      onPaginationModelChange={onPaginationModelChange}
      filterModel={filterModel}
      onFilterModelChange={onFilterModelChange}
      sortModel={sortModel}
      onSortModelChange={setSortModel}
      initialColumnVisibility={getColumnDefaultVisibility()}
      exportFileName={`payments-export-${dayjs().format("YYYY-MM-DD")}`}
      noRowsOverlay={<CustomNoRowsOverlay />}
      toolbarActions={
        <AddEntityActions
          addEntityForm={
            <PaymentForm
              control={paymentControl}
              practices={practices}
              supportedPayers={supportedPayers}
              supportedSources={supportedSources}
              setPaymentFileToUpload={setPaymentFileToUpload}
              paymentFileToUpload={paymentFileToUpload}
              setFileError={setFileError}
              fileError={fileError}
              isEdit={isEdit}
            />
          }
          addAIForm={
            <AIPaymentForm
              control={aiControl}
              practices={practices}
              supportedPayers={supportedPayers}
              supportedSources={supportedSources}
              setFileToUpload={setAIFileToUpload}
              fileToUpload={aiFileToUpload}
              setFileError={setAIFileError}
              fileError={aiFileError}
            />
          }
          handleSubmit={onPaymentFormSubmission}
          handleAISubmit={onAIFormSubmission}
          isDisabled={paymentIsDisabled}
          isAIDisabled={Boolean(aiIsDisabled)}
          entityName="Payment"
          enableAddToolBar
          setOpenModal={onPaymentModal}
          openModal={openPaymentModal}
          setOpenAIModal={onAIModal}
          openAIModal={openAIModal}
          enableAddWithAI
        />
      }
      footer={
        <Pagination
          color="primary"
          count={Math.max(1, Math.ceil(rowCount / effectivePageSize))}
          page={effectivePage}
          onChange={(_event, value) => pagination?.onPageChange?.(value)}
        />
      }
    />
  );
}

PaymentsTable.defaultProps = {
  persistKey: undefined,
};
